<template>
  <v-card class="mt-6 shadow create-result-card">
    <v-card-title class="create-result-header">
      <div>
        <div class="overline primary--text mb-1">Game Result</div>
        <div class="text-h6 font-weight-bold">Create Result</div>
        <div
          class="text-caption grey--text text--darken-1 mt-1 create-result-subtitle"
        >
          Enter one digit per team for each quarter.
        </div>
      </div>

      <div class="create-result-header__meta">
        <v-chip small label class="mb-2" :class="gameStateClass">
          <v-icon left x-small>{{ gameStateIcon }}</v-icon>
          {{ gameStateLabel }}
        </v-chip>

        <v-chip small label class="mb-2" :class="progressChipClass">
          <v-icon left x-small>mdi-timer-sand</v-icon>
          {{ completedQuarterCount }}/{{ quarters.length }} Quarters Ready
        </v-chip>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="py-4">
      <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
        {{ errorMessage }}
      </v-alert>

      <v-sheet class="create-result-summary pa-3 mb-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="summary-label">Matchup</div>
            <div class="summary-value summary-value--compact">
              {{ teamALabel }} vs {{ teamBLabel }}
            </div>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <div class="summary-label">Game ID</div>
            <div class="summary-value summary-value--compact">
              #{{ gameId || "-" }}
            </div>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <div class="summary-label">Current Snapshot</div>
            <div class="summary-value summary-value--compact">
              {{ resultSummaryText }}
            </div>
          </v-col>
        </v-row>
      </v-sheet>

      <v-alert v-if="loadingResult" type="info" dense outlined class="mb-4">
        Loading result details...
      </v-alert>

      <div v-if="resultDetails" class="mb-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-sheet class="result-stat-card pa-3 fill-height">
              <div class="result-stat-card__label">Result ID</div>
              <div class="result-stat-card__value">#{{ resultDetails.id }}</div>
              <div class="result-stat-card__hint">
                Linked to game #{{ resultDetails.game_id }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" md="4">
            <v-sheet class="result-stat-card pa-3 fill-height">
              <div class="result-stat-card__label">Approval Status</div>
              <div class="mt-2">
                <v-chip small :class="resultStatusClass">
                  {{ resultDetails.status }}
                </v-chip>
              </div>
              <div class="result-stat-card__hint mt-2">
                Created {{ formattedResultCreatedAt }}
              </div>
            </v-sheet>
          </v-col>

          <v-col cols="12" md="4">
            <v-sheet
              class="result-stat-card pa-3 fill-height result-stat-card--accent"
            >
              <div class="result-stat-card__label">Result Combination</div>
              <div
                class="result-stat-card__value result-stat-card__value--tight"
              >
                {{ resultDetails.result_combination || "-" }}
              </div>
              <div class="result-stat-card__hint">
                Review the per-quarter breakdown below.
              </div>
            </v-sheet>
          </v-col>
        </v-row>

        <v-sheet class="quarter-board pa-3 mt-3">
          <div class="d-flex justify-space-between align-center flex-wrap mb-3">
            <div>
              <div class="text-subtitle-2 font-weight-bold">
                Quarter Breakdown
              </div>
              <div class="text-caption grey--text text--darken-1">
                Saved quarter scores for this Otso Panalo game result.
              </div>
            </div>

            <v-chip small label class="quarter-board__chip">
              {{ resultDetails.result_combination || "No combination" }}
            </v-chip>
          </div>

          <v-row dense>
            <v-col
              v-for="quarter in resultQuarterDetails"
              :key="quarter.label"
              cols="12"
              sm="6"
              md="3"
            >
              <div class="quarter-board__item pa-2">
                <div class="quarter-board__label">{{ quarter.label }}</div>
                <div class="quarter-board__value">{{ quarter.value }}</div>
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <v-row dense class="mt-3">
          <v-col cols="12" md="6">
            <v-btn
              v-if="canEditResult && resultDetails.status !== 'Approved'"
              block
              outlined
              color="primary"
              class="result-action-btn result-action-btn--compact"
              :disabled="isSubmitting"
              @click="openEditDialog"
            >
              <v-icon left>mdi-pencil-outline</v-icon>
              Edit Result
            </v-btn>
          </v-col>

          <v-col cols="12" md="6">
            <v-btn
              v-if="canApproveResult && resultDetails.status === 'For Approval'"
              block
              color="success"
              class="result-action-btn result-action-btn--compact"
              :loading="approveLoading"
              :disabled="isSubmitting"
              @click="openApproveDialog"
            >
              <v-icon left>mdi-check-circle-outline</v-icon>
              Approve Result
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-alert v-else-if="isClosed" type="info" dense outlined class="mb-0">
        {{ closedStateMessage }}
      </v-alert>

      <v-form
        v-else
        ref="form"
        v-model="valid"
        lazy-validation
        @submit.prevent="openCreateDialog"
      >
        <v-sheet class="result-preview-panel pa-3 mb-4">
          <div class="preview-strip mb-2">
            <div>
              <div class="text-subtitle-2 font-weight-bold">Live Preview</div>
              <div class="text-caption grey--text text--darken-1">
                {{ completedQuarterCount }}/{{ quarters.length }} quarters ready
              </div>
            </div>

            <v-chip small label :class="progressChipClass">
              {{ completionPercent }}%
            </v-chip>
          </div>

          <v-progress-linear
            rounded
            height="8"
            color="primary"
            background-color="grey lighten-2"
            :value="completionPercent"
          ></v-progress-linear>

          <v-row dense class="mt-2">
            <v-col
              v-for="quarter in quarters"
              :key="quarter.key"
              cols="12"
              sm="6"
              md="3"
            >
              <div
                class="preview-pill pa-2"
                :class="{
                  'preview-pill--complete': quarterIsComplete(quarter),
                  'preview-pill--partial':
                    quarterHasAnyInput(quarter) && !quarterIsComplete(quarter),
                }"
              >
                <div class="preview-pill__label">{{ quarter.label }}</div>
                <div class="preview-pill__value">
                  {{ formattedQuarterResult(quarter) || "--" }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-alert
            v-if="hasPartialQuarter"
            type="warning"
            dense
            text
            class="mt-2 mb-0 preview-warning"
          >
            Complete both digits for any quarter marked incomplete.
          </v-alert>
        </v-sheet>

        <v-row dense>
          <v-col
            v-for="quarter in quarters"
            :key="quarter.key"
            cols="12"
            md="6"
          >
            <v-sheet class="quarter-entry pa-3 fill-height mb-3">
              <div
                class="d-flex justify-space-between align-center flex-wrap mb-3"
              >
                <div>
                  <div class="text-subtitle-2 font-weight-bold">
                    {{ quarter.label }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    Enter the last digit for both teams.
                  </div>
                </div>

                <v-chip x-small label :class="quarterStateClass(quarter)">
                  {{ quarterStateLabel(quarter) }}
                </v-chip>
              </div>

              <v-row dense align="center">
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="form[quarter.inputOne]"
                    :rules="quarterInputRules"
                    :disabled="isSubmitting"
                    label="Winning Score"
                    maxlength="1"
                    outlined
                    dense
                    hide-details="auto"
                    counter="1"
                    inputmode="numeric"
                    type="text"
                    class="quarter-entry__input"
                    @input="normalizeInput(quarter.inputOne)"
                  ></v-text-field>
                </v-col>

                <v-col
                  cols="12"
                  sm="2"
                  class="text-center quarter-entry__separator"
                >
                  <v-icon color="grey darken-1">mdi-close</v-icon>
                </v-col>

                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="form[quarter.inputTwo]"
                    :rules="quarterInputRules"
                    :disabled="isSubmitting"
                    label="Losing Score"
                    maxlength="1"
                    outlined
                    dense
                    hide-details="auto"
                    counter="1"
                    inputmode="numeric"
                    type="text"
                    class="quarter-entry__input"
                    @input="normalizeInput(quarter.inputTwo)"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="quarter-entry__footer mt-1">
                <span class="text-caption grey--text text--darken-1"
                  >Formatted result</span
                >
                <v-chip small label class="quarter-entry__result-chip">
                  {{ formattedQuarterResult(quarter) || "--" }}
                </v-chip>
              </div>
            </v-sheet>
          </v-col>
        </v-row>

        <v-row dense class="mt-1">
          <v-col cols="12" md="6">
            <v-btn
              block
              color="primary"
              class="result-action-btn result-action-btn--compact"
              type="submit"
              :loading="submitLoading"
              :disabled="isSubmitting"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Save Result
            </v-btn>
          </v-col>

          <v-col cols="12" md="6">
            <v-btn
              block
              outlined
              color="black"
              class="result-action-btn result-action-btn--compact"
              :disabled="isSubmitting"
              @click="resetForm"
            >
              <v-icon left>mdi-refresh</v-icon>
              Clear Inputs
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <ConfirmationMessage
      v-if="confirmationDialog"
      :confirmationData="confirmationData"
      @closeConfirmationDialog="confirmationDialog = false"
      @onSubmit="handleConfirmationSubmit"
    />

    <v-dialog v-model="editDialog" max-width="860" scrollable>
      <v-card class="edit-result-dialog">
        <v-card-title class="edit-result-dialog__header">
          <div>
            <div class="overline primary--text mb-1">Game Result</div>
            <div class="text-h6 font-weight-bold">Edit Result</div>
            <div class="text-caption grey--text text--darken-1 mt-1">
              Update the quarter digits while the result is still pending
              approval.
            </div>
          </div>

          <v-chip small label :class="resultStatusClass">
            {{ resultDetails ? resultDetails.status : "For Approval" }}
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
            {{ errorMessage }}
          </v-alert>

          <v-form
            ref="editForm"
            v-model="valid"
            lazy-validation
            @submit.prevent="submitUpdatedResult"
          >
            <v-sheet class="result-preview-panel pa-3 mb-4">
              <div class="preview-strip mb-2">
                <div>
                  <div class="text-subtitle-2 font-weight-bold">
                    Updated Preview
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    {{ completedQuarterCount }}/{{ quarters.length }} quarters
                    ready
                  </div>
                </div>

                <v-chip small label :class="progressChipClass">
                  {{ completionPercent }}%
                </v-chip>
              </div>

              <v-progress-linear
                rounded
                height="8"
                color="primary"
                background-color="grey lighten-2"
                :value="completionPercent"
              ></v-progress-linear>

              <v-row dense class="mt-2">
                <v-col
                  v-for="quarter in quarters"
                  :key="`${quarter.key}-dialog`"
                  cols="12"
                  sm="6"
                  md="3"
                >
                  <div
                    class="preview-pill pa-2"
                    :class="{
                      'preview-pill--complete': quarterIsComplete(quarter),
                      'preview-pill--partial':
                        quarterHasAnyInput(quarter) &&
                        !quarterIsComplete(quarter),
                    }"
                  >
                    <div class="preview-pill__label">{{ quarter.label }}</div>
                    <div class="preview-pill__value">
                      {{ formattedQuarterResult(quarter) || "--" }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-alert
                v-if="hasPartialQuarter"
                type="warning"
                dense
                text
                class="mt-2 mb-0 preview-warning"
              >
                Complete both digits for any quarter marked incomplete.
              </v-alert>
            </v-sheet>

            <v-row dense>
              <v-col
                v-for="quarter in quarters"
                :key="`${quarter.key}-edit`"
                cols="12"
                md="6"
              >
                <v-sheet class="quarter-entry pa-3 fill-height mb-3">
                  <div
                    class="d-flex justify-space-between align-center flex-wrap mb-3"
                  >
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        {{ quarter.label }}
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        Enter the last digit for both teams.
                      </div>
                    </div>

                    <v-chip x-small label :class="quarterStateClass(quarter)">
                      {{ quarterStateLabel(quarter) }}
                    </v-chip>
                  </div>

                  <v-row dense align="center">
                    <v-col cols="12" sm="5">
                      <v-text-field
                        v-model="form[quarter.inputOne]"
                        :rules="quarterInputRules"
                        :disabled="isSubmitting"
                        :label="teamALabel"
                        maxlength="1"
                        outlined
                        dense
                        hide-details="auto"
                        counter="1"
                        inputmode="numeric"
                        type="text"
                        class="quarter-entry__input"
                        @input="normalizeInput(quarter.inputOne)"
                      ></v-text-field>
                    </v-col>

                    <v-col
                      cols="12"
                      sm="2"
                      class="text-center quarter-entry__separator"
                    >
                      <v-icon color="grey darken-1">mdi-close</v-icon>
                    </v-col>

                    <v-col cols="12" sm="5">
                      <v-text-field
                        v-model="form[quarter.inputTwo]"
                        :rules="quarterInputRules"
                        :disabled="isSubmitting"
                        :label="teamBLabel"
                        maxlength="1"
                        outlined
                        dense
                        hide-details="auto"
                        counter="1"
                        inputmode="numeric"
                        type="text"
                        class="quarter-entry__input"
                        @input="normalizeInput(quarter.inputTwo)"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <div class="quarter-entry__footer mt-1">
                    <span class="text-caption grey--text text--darken-1"
                      >Formatted result</span
                    >
                    <v-chip small label class="quarter-entry__result-chip">
                      {{ formattedQuarterResult(quarter) || "--" }}
                    </v-chip>
                  </div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-5 pt-0 d-flex justify-end">
          <v-btn
            outlined
            color="black"
            :disabled="isSubmitting"
            @click="closeEditDialog"
          >
            Close
          </v-btn>

          <v-btn
            color="primary"
            class="ml-2"
            :loading="updateLoading"
            :disabled="isSubmitting"
            @click="submitUpdatedResult"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import formatDate from "@/helpers/formatDate";
import formRules from "@/helpers/formRules";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";
import ConfirmationMessage from "@/components/ConfirmationMessage";

const createEmptyForm = () => ({
  firstQuarterInputOne: "",
  firstQuarterInputTwo: "",
  secondQuarterInputOne: "",
  secondQuarterInputTwo: "",
  thirdQuarterInputOne: "",
  thirdQuarterInputTwo: "",
  fourthQuarterInputOne: "",
  fourthQuarterInputTwo: "",
});

export default {
  components: {
    ConfirmationMessage,
  },

  props: {
    gameId: {
      type: [String, Number],
      required: true,
    },
    gameDetails: {
      type: Object,
      default: () => ({}),
    },
    gameStatus: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    valid: true,
    loadingResult: false,
    submitLoading: false,
    updateLoading: false,
    approveLoading: false,
    editDialog: false,
    confirmationDialog: false,
    confirmationAction: "create",
    errorMessage: "",
    confirmationData: {},
    resultDetails: null,
    form: createEmptyForm(),
  }),

  computed: {
    ...mapState(["accountInfo"]),

    isFinished() {
      return this.gameStatus === "Finished";
    },

    isCancelled() {
      return this.gameStatus === "Cancelled";
    },

    isClosed() {
      return this.isFinished || this.isCancelled;
    },

    isSubmitting() {
      return (
        this.disabled ||
        this.submitLoading ||
        this.updateLoading ||
        this.approveLoading
      );
    },

    gameStateLabel() {
      if (this.resultDetails) {
        return "Result Saved";
      }

      if (this.isCancelled) {
        return "Cancelled";
      }

      return this.isFinished ? "Finished" : "Pending Result";
    },

    gameStateIcon() {
      if (this.resultDetails) {
        return "mdi-check-circle-outline";
      }

      if (this.isCancelled) {
        return "mdi-cancel";
      }

      return this.isFinished ? "mdi-flag-checkered" : "mdi-progress-clock";
    },

    gameStateClass() {
      if (this.resultDetails) {
        return "green lighten-5 green--text";
      }

      if (this.isCancelled) {
        return "red lighten-5 red--text";
      }

      return this.isFinished
        ? "blue lighten-5 blue--text text--darken-2"
        : "orange lighten-5 orange--text";
    },

    closedStateMessage() {
      return this.isCancelled
        ? "This game is cancelled. Result creation is no longer available."
        : "This game is already finished. Result creation is no longer available.";
    },

    completedQuarterCount() {
      if (this.resultDetails && !this.editDialog) {
        return this.quarters.filter(
          (quarter) => !!this.resultDetails[quarter.payloadKey],
        ).length;
      }

      return this.quarters.filter((quarter) => this.quarterIsComplete(quarter))
        .length;
    },

    completionPercent() {
      return Math.round(
        (this.completedQuarterCount / this.quarters.length) * 100,
      );
    },

    progressChipClass() {
      if (this.completedQuarterCount === this.quarters.length) {
        return "green lighten-5 green--text";
      }

      if (this.completedQuarterCount > 0 || this.hasPartialQuarter) {
        return "orange lighten-5 orange--text";
      }

      return "blue-grey lighten-5 blue-grey--text text--darken-1";
    },

    hasPartialQuarter() {
      if (this.resultDetails && !this.editDialog) {
        return false;
      }

      return this.quarters.some(
        (quarter) =>
          this.quarterHasAnyInput(quarter) && !this.quarterIsComplete(quarter),
      );
    },

    canApproveResult() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(112)
      );
    },

    canEditResult() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(106)
      );
    },

    resultStatusClass() {
      return this.resultDetails && this.resultDetails.status === "Approved"
        ? "green lighten-5 green--text"
        : "orange lighten-5 orange--text";
    },

    formattedResultCreatedAt() {
      return this.resultDetails && this.resultDetails.created_at
        ? formatDate(this.resultDetails.created_at, true)
        : "-";
    },

    resultSummaryText() {
      if (this.resultDetails) {
        return this.resultDetails.result_combination || "Saved result";
      }

      if (this.isClosed) {
        return "Game closed";
      }

      if (!this.completedQuarterCount && !this.hasPartialQuarter) {
        return "No quarter input yet";
      }

      return `${this.completedQuarterCount} of ${this.quarters.length} quarters ready`;
    },

    quarterInputRules() {
      return [
        formRules.required,
        formRules.numeric,
        (value) =>
          String(value || "").trim().length === 1 ||
          "This field must contain 1 digit only.",
      ];
    },

    teamALabel() {
      return this.gameDetails.team_a_name || "Team A";
    },

    teamBLabel() {
      return this.gameDetails.team_b_name || "Team B";
    },

    quarters() {
      return [
        {
          key: "first",
          label: "1st Quarter",
          inputOne: "firstQuarterInputOne",
          inputTwo: "firstQuarterInputTwo",
          payloadKey: "first_quarter_result",
        },
        {
          key: "second",
          label: "2nd Quarter",
          inputOne: "secondQuarterInputOne",
          inputTwo: "secondQuarterInputTwo",
          payloadKey: "second_quarter_result",
        },
        {
          key: "third",
          label: "3rd Quarter",
          inputOne: "thirdQuarterInputOne",
          inputTwo: "thirdQuarterInputTwo",
          payloadKey: "third_quarter_result",
        },
        {
          key: "fourth",
          label: "4th Quarter",
          inputOne: "fourthQuarterInputOne",
          inputTwo: "fourthQuarterInputTwo",
          payloadKey: "fourth_quarter_result",
        },
      ];
    },

    resultQuarterDetails() {
      if (!this.resultDetails) {
        return [];
      }

      return [
        {
          label: "1st Quarter",
          value: this.resultDetails.first_quarter_result || "-",
        },
        {
          label: "2nd Quarter",
          value: this.resultDetails.second_quarter_result || "-",
        },
        {
          label: "3rd Quarter",
          value: this.resultDetails.third_quarter_result || "-",
        },
        {
          label: "4th Quarter",
          value: this.resultDetails.fourth_quarter_result || "-",
        },
      ];
    },

    confirmationText() {
      return this.quarters
        .map(
          (quarter) =>
            `${quarter.label}: ${this.formattedQuarterResult(quarter)}`,
        )
        .join(" | ");
    },
  },

  created() {
    this.loadResultDetails();
  },

  watch: {
    gameId() {
      this.editDialog = false;
      this.resetForm();
      this.resultDetails = null;
      this.errorMessage = "";
      this.loadResultDetails();
    },
  },

  methods: {
    async loadResultDetails() {
      if (!this.gameId) {
        this.resultDetails = null;
        return;
      }

      try {
        this.loadingResult = true;
        const response = await responseGet(
          "",
          `/otso_panalo/results/details/${this.gameId}`,
        );

        if (response.status === 200) {
          this.resultDetails = response.data.details || null;
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        }
      } catch (error) {
        this.resultDetails = null;
      } finally {
        this.loadingResult = false;
      }
    },

    normalizeInput(field) {
      this.form[field] = String(this.form[field] || "")
        .replace(/\D/g, "")
        .slice(0, 1);
    },

    quarterHasAnyInput(quarter) {
      return Boolean(
        String(this.form[quarter.inputOne] || "").trim() ||
          String(this.form[quarter.inputTwo] || "").trim(),
      );
    },

    quarterIsComplete(quarter) {
      return Boolean(this.formattedQuarterResult(quarter));
    },

    quarterStateClass(quarter) {
      if (this.quarterIsComplete(quarter)) {
        return "green lighten-5 green--text";
      }

      if (this.quarterHasAnyInput(quarter)) {
        return "orange lighten-5 orange--text";
      }

      return "grey lighten-4 grey--text text--darken-1";
    },

    quarterStateLabel(quarter) {
      if (this.quarterIsComplete(quarter)) {
        return "Ready";
      }

      if (this.quarterHasAnyInput(quarter)) {
        return "Incomplete";
      }

      return "Empty";
    },

    formattedQuarterResult(quarter) {
      const inputOne = String(this.form[quarter.inputOne] || "").trim();
      const inputTwo = String(this.form[quarter.inputTwo] || "").trim();

      if (!inputOne || !inputTwo) {
        return "";
      }

      return `${inputOne}-${inputTwo}`;
    },

    getMessage(payload, fallback) {
      if (!payload) {
        return fallback;
      }

      if (payload.messages) {
        const firstKey = Object.keys(payload.messages)[0];
        if (firstKey && typeof payload.messages[firstKey] === "string") {
          return payload.messages[firstKey];
        }
      }

      if (typeof payload.message === "string") {
        return payload.message;
      }

      return fallback;
    },

    buildFormData() {
      const formData = new FormData();
      formData.append("game_id", this.gameId);

      this.quarters.forEach((quarter) => {
        formData.append(
          quarter.payloadKey,
          this.formattedQuarterResult(quarter),
        );
      });

      return formData;
    },

    buildUpdateFormData() {
      const formData = new FormData();
      formData.append("id", this.resultDetails.id);

      this.quarters.forEach((quarter) => {
        formData.append(
          quarter.payloadKey,
          this.formattedQuarterResult(quarter),
        );
      });

      return formData;
    },

    populateFormFromResultDetails(details = this.resultDetails) {
      const nextForm = createEmptyForm();

      this.quarters.forEach((quarter) => {
        const [inputOne = "", inputTwo = ""] = String(
          details && details[quarter.payloadKey]
            ? details[quarter.payloadKey]
            : "",
        ).split("-");

        nextForm[quarter.inputOne] = /^\d$/.test(inputOne) ? inputOne : "";
        nextForm[quarter.inputTwo] = /^\d$/.test(inputTwo) ? inputTwo : "";
      });

      this.form = nextForm;

      if (this.$refs.editForm) {
        this.$refs.editForm.resetValidation();
      }
    },

    openCreateDialog() {
      if (!this.$refs.form.validate() || this.isSubmitting) {
        return;
      }

      this.errorMessage = "";
      this.confirmationAction = "create";
      this.confirmationData = {
        title: "Confirm Result Submission",
        text: `Please confirm the quarter results. ${this.confirmationText}`,
        closeBtnText: "Cancel",
        closeBtnColor: "grey lighten-1",
        saveBtnText: "Confirm",
        saveBtnColor: "primary",
      };
      this.confirmationDialog = true;
    },

    openEditDialog() {
      if (
        !this.resultDetails ||
        this.resultDetails.status === "Approved" ||
        this.isSubmitting
      ) {
        return;
      }

      this.errorMessage = "";
      this.populateFormFromResultDetails();
      this.editDialog = true;
    },

    closeEditDialog() {
      if (this.updateLoading) {
        return;
      }

      this.editDialog = false;
      this.errorMessage = "";

      if (this.$refs.editForm) {
        this.$refs.editForm.resetValidation();
      }
    },

    openApproveDialog() {
      if (!this.resultDetails || this.isSubmitting) {
        return;
      }

      this.errorMessage = "";
      this.confirmationAction = "approve";
      this.confirmationData = {
        title: "Confirm Result Approval",
        text: `Approve this result combination: ${
          this.resultDetails.result_combination || "-"
        }?`,
        closeBtnText: "Cancel",
        closeBtnColor: "grey lighten-1",
        saveBtnText: "Approve",
        saveBtnColor: "success",
      };
      this.confirmationDialog = true;
    },

    async handleConfirmationSubmit() {
      if (this.confirmationAction === "approve") {
        await this.submitApproval();
        return;
      }

      await this.submitResult();
    },

    async submitResult() {
      if (this.isSubmitting) {
        return;
      }

      try {
        this.submitLoading = true;
        this.errorMessage = "";

        const response = await responsePost(
          this.buildFormData(),
          "otso_panalo/games/create_result",
        );

        if (response.status === 200) {
          this.confirmationDialog = false;
          this.resultDetails = response.data;
          this.$emit("created", response.data);
          this.resetForm(false);
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.errorMessage = this.getMessage(
            response.data,
            "Failed to create Otso Panalo result.",
          );
          this.confirmationDialog = false;
        }
      } catch (error) {
        this.errorMessage = "Failed to create Otso Panalo result.";
        this.confirmationDialog = false;
      } finally {
        this.submitLoading = false;
      }
    },

    async submitUpdatedResult() {
      if (
        !this.resultDetails ||
        this.resultDetails.status === "Approved" ||
        this.isSubmitting ||
        !this.$refs.editForm.validate()
      ) {
        return;
      }

      try {
        this.updateLoading = true;
        this.errorMessage = "";

        const response = await responsePost(
          this.buildUpdateFormData(),
          "otso_panalo/results/update",
        );

        if (response.status === 200) {
          this.resultDetails = response.data.details || this.resultDetails;
          this.editDialog = false;
          this.$emit("updated", this.resultDetails);
          this.resetForm(false);
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.errorMessage = this.getMessage(
            response.data,
            "Failed to update Otso Panalo result.",
          );
        }
      } catch (error) {
        this.errorMessage = "Failed to update Otso Panalo result.";
      } finally {
        this.updateLoading = false;
      }
    },

    async submitApproval() {
      if (!this.resultDetails || this.isSubmitting) {
        return;
      }

      try {
        this.approveLoading = true;
        this.errorMessage = "";

        const formData = new FormData();
        formData.append("id", this.resultDetails.id);

        const response = await responsePost(
          formData,
          "otso_panalo/results/approve",
        );

        if (response.status === 200) {
          this.resultDetails = response.data.details || this.resultDetails;
          this.confirmationDialog = false;
          this.$emit("approved", this.resultDetails);
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.errorMessage = this.getMessage(
            response.data,
            "Failed to approve Otso Panalo result.",
          );
          this.confirmationDialog = false;
        }
      } catch (error) {
        this.errorMessage = "Failed to approve Otso Panalo result.";
        this.confirmationDialog = false;
      } finally {
        this.approveLoading = false;
      }
    },

    resetForm(resetValidation = true) {
      this.form = createEmptyForm();
      this.errorMessage = "";

      if (resetValidation && this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
.create-result-card {
  overflow: hidden;
}

.create-result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px 16px;
  background: linear-gradient(135deg, #f7fbff 0%, #eef6ff 45%, #f8fbf4 100%);
}

.create-result-header__meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.create-result-subtitle {
  max-width: 480px;
}

.edit-result-dialog__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 24px 16px;
}

.create-result-summary,
.result-stat-card,
.quarter-board,
.result-preview-panel,
.quarter-entry {
  border: 1px solid #e6edf5;
  border-radius: 14px;
  background: #ffffff;
}

.create-result-summary {
  background: linear-gradient(135deg, #ffffff 0%, #f9fbff 100%);
}

.summary-label,
.result-stat-card__label,
.quarter-board__label,
.preview-pill__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #607d8b;
}

.summary-value,
.result-stat-card__value,
.quarter-board__value,
.preview-pill__value {
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #102a43;
  line-height: 1.2;
}

.summary-value--compact {
  font-size: 16px;
}

.result-stat-card__value--tight {
  font-size: 16px;
  word-break: break-word;
}

.result-stat-card__hint {
  font-size: 12px;
  color: #627d98;
}

.result-stat-card--accent {
  background: linear-gradient(135deg, #fffdf6 0%, #fff7dd 100%);
}

.quarter-board__chip {
  background: #eef4ff;
  color: #1d4ed8;
}

.quarter-board__item {
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e6edf5;
}

.preview-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.preview-pill {
  border-radius: 12px;
  border: 1px solid #d8e1ea;
  background: #f8fafc;
  height: 100%;
}

.preview-pill--complete {
  border-color: #9ad4b0;
  background: #f1fbf4;
}

.preview-pill--partial {
  border-color: #ffd18a;
  background: #fff8ea;
}

.quarter-entry {
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.quarter-entry__separator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.quarter-entry__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.quarter-entry__result-chip {
  background: #eef4ff;
  color: #1d4ed8;
  font-weight: 700;
}

.result-action-btn {
  letter-spacing: 0.02em;
}

.result-action-btn--compact {
  height: 42px !important;
}

.preview-warning {
  font-size: 12px;
}

@media (max-width: 959px) {
  .create-result-header {
    padding: 16px;
  }

  .create-result-header__meta {
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .create-result-header {
    flex-direction: column;
  }

  .summary-value,
  .result-stat-card__value,
  .quarter-board__value,
  .preview-pill__value {
    font-size: 16px;
  }

  .quarter-entry__footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
